<template>
  <b-card
    no-body
    class="team-leader-alerts-panel team-snapshot-panel team-mid-panel h-100 mb-0"
    :class="{ 'team-snapshot-panel--mobile-collapsed': mobileSnapshotCollapsed }"
  >
    <b-card-header
      class="team-mid-panel__header d-flex align-items-center justify-content-between py-50 px-1 border-bottom"
      :class="{ 'team-mid-panel__header--toggle': isNarrowViewport }"
      :role="isNarrowViewport ? 'button' : null"
      :tabindex="isNarrowViewport ? 0 : null"
      :aria-expanded="isNarrowViewport ? String(!mobileSnapshotCollapsed) : null"
      @click="toggleMobileSnapshot"
      @keydown.enter.prevent="toggleMobileSnapshot"
      @keydown.space.prevent="toggleMobileSnapshot"
    >
      <h6 class="mb-0 d-flex align-items-center text-body team-mid-panel__title">
        <feather-icon icon="ActivityIcon" size="14" class="mr-50 text-primary" />
        {{ panelTitle }}
        <b-badge
          v-if="headerBadgeCount > 0"
          variant="light-warning"
          pill
          class="ml-50 team-alerts-count-badge"
        >
          {{ headerBadgeCount }}
        </b-badge>
      </h6>
      <feather-icon
        v-if="isNarrowViewport"
        :icon="mobileSnapshotCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
        size="16"
        class="text-muted team-snapshot-collapse-chevron flex-shrink-0"
      />
    </b-card-header>

    <b-card-body
      v-show="!isNarrowViewport || !mobileSnapshotCollapsed"
      class="team-mid-panel__body p-1 d-flex flex-column"
      :aria-hidden="isNarrowViewport && mobileSnapshotCollapsed ? 'true' : null"
    >
      <!-- Needs attention -->
      <section class="team-snapshot-section team-snapshot-attention flex-shrink-0">
        <div class="team-snapshot-section-label">Needs attention</div>
        <div class="team-snapshot-metrics">
          <button
            type="button"
            class="team-snapshot-metric"
            :class="{
              'team-snapshot-metric--active': pendingLeaveCount > 0,
              'team-snapshot-metric--muted': pendingLeaveCount === 0,
            }"
            :disabled="pendingLeaveCount === 0"
            @click="onPendingLeavesClick"
          >
            <span class="team-snapshot-metric__label">Leave approvals</span>
            <b-badge
              :variant="pendingLeaveCount > 0 ? 'warning' : 'light-secondary'"
              pill
              class="team-snapshot-metric__badge"
            >
              {{ pendingLeaveCount }}
            </b-badge>
          </button>

          <button
            type="button"
            class="team-snapshot-metric"
            :class="{
              'team-snapshot-metric--active': probationCount > 0,
              'team-snapshot-metric--muted': probationCount === 0,
              'team-snapshot-metric--open': showProbationList && probationCount > 0,
            }"
            :disabled="probationCount === 0"
            @click="toggleProbationList"
          >
            <span class="team-snapshot-metric__label">
              People on Probation
              <feather-icon
                v-if="probationCount > 0"
                :icon="showProbationList ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="12"
                class="ml-25 team-snapshot-metric__chevron"
              />
            </span>
            <b-badge
              :variant="probationCount > 0 ? 'info' : 'light-secondary'"
              pill
              class="team-snapshot-metric__badge"
            >
              {{ probationCount }}
            </b-badge>
          </button>
        </div>

        <div
          v-if="probationCount === 0 && probationSourceMissing"
          class="small text-muted mt-50 team-snapshot-probation-empty"
        >
          No probation data from HR
        </div>

        <div
          v-if="showProbationList && probationCount > 0"
          class="team-snapshot-probation-list mt-50"
          :class="{ 'team-snapshot-probation-list--expanded': probationListExpanded }"
        >
          <div
            v-for="item in visibleProbationItems"
            :key="`prob-${item.emp_id || item.probationEndDate}`"
            class="team-snapshot-probation-row d-flex align-items-center justify-content-between"
          >
            <span class="small text-truncate font-weight-bolder mr-50">
              {{ probationName(item) }}
            </span>
            <span class="small text-muted text-nowrap team-snapshot-probation-days">
              {{ probationDaysLabel(item) }}
            </span>
          </div>
          <button
            v-if="hiddenProbationCount > 0 && !probationListExpanded"
            type="button"
            class="team-snapshot-probation-more"
            @click.stop="openProbationManagement"
          >
            Show all {{ probationCount }}
          </button>
          <button
            v-else-if="probationListExpanded && probationCount > PROBATION_VISIBLE_CAP"
            type="button"
            class="team-snapshot-probation-more"
            @click.stop="collapseProbationList"
          >
            Show less
          </button>
        </div>
      </section>

      <!-- Alerts & Notifications -->
      <section class="team-snapshot-section team-snapshot-alerts flex-shrink-0">
        <div class="d-flex align-items-center justify-content-between mb-25">
          <div class="team-snapshot-section-label mb-0">Alerts &amp; Notifications</div>
          <b-badge
            v-if="alertTotalCount > 0"
            variant="light-secondary"
            pill
            class="team-alerts-count-badge"
          >
            {{ alertTotalCount }}
          </b-badge>
        </div>

        <div
          v-if="alertTotalCount === 0"
          class="team-snapshot-all-clear text-muted small"
        >
          <feather-icon icon="CheckCircleIcon" size="12" class="mr-25 text-success" />
          No team alerts right now
        </div>

        <template v-else>
          <b-list-group flush class="team-alerts-list">
            <b-list-group-item
              v-for="item in visibleAlertItems"
              :key="item.key"
              class="d-flex align-items-start px-50 py-25 border-0"
            >
              <b-badge
                :variant="item.badgeVariant"
                class="mr-50 flex-shrink-0 text-uppercase team-alert-type-badge"
              >
                {{ item.typeLabel }}
              </b-badge>
              <div class="flex-grow-1 min-w-0">
                <div class="d-flex align-items-center justify-content-between">
                  <span
                    class="small font-weight-bolder text-truncate mr-50"
                  >
                    {{ item.primary }}
                  </span>
                  <b-badge
                    :variant="item.severityVariant"
                    pill
                    class="flex-shrink-0 team-alert-severity-badge"
                  >
                    {{ item.severityLabel }}
                  </b-badge>
                </div>
                <div
                  v-if="item.reason"
                  class="small text-muted text-truncate"
                >
                  {{ item.reason }}
                </div>
              </div>
            </b-list-group-item>
          </b-list-group>
          <div v-if="hiddenAlertCount > 0" class="mt-25">
            <span class="team-alerts-more text-muted small">+{{ hiddenAlertCount }} more</span>
          </div>
        </template>
      </section>

      <!-- Useful extras: upcoming leaves / birthdays / announcements -->
      <section
        v-if="showExtrasSection"
        class="team-snapshot-section team-snapshot-extras flex-shrink-0"
      >
        <div class="team-snapshot-section-label">Coming up</div>

        <!-- Upcoming leaves (next 7 days) -->
        <div v-if="upcomingLeavesNext7.length > 0" class="team-snapshot-extra-block">
          <div class="d-flex align-items-center justify-content-between mb-25">
            <span class="team-snapshot-extra-label">
              <feather-icon icon="CalendarIcon" size="11" class="mr-25 text-primary" />
              Leaves (7 days)
            </span>
            <b-badge variant="light-primary" pill class="team-alerts-count-badge">
              {{ upcomingLeavesNext7.length }}
            </b-badge>
          </div>
          <div
            v-for="leave in visibleUpcomingLeaves"
            :key="`upl-${leave.id || leave.emp_id}-${leave.from_date}`"
            class="team-snapshot-extra-row d-flex align-items-center justify-content-between"
            role="button"
            tabindex="0"
            @click="$emit('leave-click', leave)"
            @keydown.enter.prevent="$emit('leave-click', leave)"
          >
            <span class="small text-truncate font-weight-bolder mr-50">
              {{ leaveRowPrimary(leave) }}
            </span>
            <span class="small text-muted text-nowrap">{{ leaveRowDate(leave) }}</span>
          </div>
          <div v-if="hiddenUpcomingLeavesCount > 0" class="small text-muted mt-25 team-snapshot-probation-more">
            +{{ hiddenUpcomingLeavesCount }} more
          </div>
        </div>

        <!-- Birthdays this week -->
        <div v-if="birthdaysThisWeek.length > 0" class="team-snapshot-extra-block">
          <div class="d-flex align-items-center justify-content-between mb-25">
            <span class="team-snapshot-extra-label">
              <feather-icon icon="GiftIcon" size="11" class="mr-25 text-primary" />
              Birthdays this week
            </span>
            <b-badge variant="light-info" pill class="team-alerts-count-badge">
              {{ birthdaysThisWeek.length }}
            </b-badge>
          </div>
          <div
            v-for="bday in visibleBirthdays"
            :key="`bday-${bday.id || bday.emp_id || bday.name}`"
            class="team-snapshot-extra-row d-flex align-items-center justify-content-between"
          >
            <span class="small text-truncate font-weight-bolder mr-50">
              {{ bday.name }}
            </span>
            <span class="small text-muted text-nowrap">{{ bday.displayDateLabel }}</span>
          </div>
          <div v-if="hiddenBirthdaysCount > 0" class="small text-muted mt-25 team-snapshot-probation-more">
            +{{ hiddenBirthdaysCount }} more
          </div>
        </div>

        <!-- Announcements teaser -->
        <button
          v-if="announcementCount > 0"
          type="button"
          class="team-snapshot-metric team-snapshot-metric--active mt-25"
          @click="$emit('announcements-click')"
        >
          <span class="team-snapshot-metric__label">
            <feather-icon icon="BellIcon" size="12" class="mr-25 text-primary" />
            Announcements
          </span>
          <b-badge variant="light-primary" pill class="team-snapshot-metric__badge">
            {{ announcementCount }}
          </b-badge>
        </button>
      </section>

      <!-- Punch In / Out filter (date + employee only) -->
      <section
        v-if="showPunchFilters"
        class="team-snapshot-section team-snapshot-punch flex-grow-1 d-flex flex-column min-h-0"
      >
        <div class="team-snapshot-punch-head d-flex align-items-center justify-content-between flex-shrink-0">
          <div class="team-snapshot-section-label mb-0">Punch in / out</div>
          <b-badge
            v-if="punchFilterActive"
            variant="light-primary"
            pill
            class="team-alerts-count-badge"
          >
            Filtered
          </b-badge>
        </div>

        <div class="team-snapshot-block flex-shrink-0">
          <b-button-group size="sm" class="team-snapshot-punch-presets w-100">
            <b-button
              v-for="preset in datePresets"
              :key="preset.value"
              :variant="activePreset === preset.value ? 'primary' : 'outline-secondary'"
              class="team-snapshot-preset-btn"
              @click="setDatePreset(preset.value)"
            >
              {{ preset.label }}
            </b-button>
          </b-button-group>

          <div class="team-snapshot-dates d-flex">
            <b-form-group
              label="From"
              label-class="team-snapshot-field-label"
              class="mb-0 team-snapshot-date-field"
            >
              <b-form-input
                v-model="punchFromDate"
                type="date"
                size="sm"
                :max="punchToDate || ''"
                @change="activePreset = null"
              />
            </b-form-group>
            <b-form-group
              label="To"
              label-class="team-snapshot-field-label"
              class="mb-0 team-snapshot-date-field"
            >
              <b-form-input
                v-model="punchToDate"
                type="date"
                size="sm"
                :min="punchFromDate || ''"
                @change="activePreset = null"
              />
            </b-form-group>
          </div>
        </div>

        <div class="team-snapshot-punch-footer d-flex align-items-end mt-auto flex-shrink-0">
          <b-form-group
            label="Employee"
            label-class="team-snapshot-field-label"
            class="mb-0 team-snapshot-employee-field flex-grow-1"
          >
            <b-form-select
              v-model="punchEmployeeFilter"
              :options="employeeOptions"
              size="sm"
            />
          </b-form-group>
          <div class="team-snapshot-punch-actions d-flex flex-shrink-0">
            <b-button
              size="sm"
              variant="primary"
              class="team-snapshot-action-btn"
              :disabled="!canApplyRosterFilter"
              @click="applyRosterFilter"
            >
              Apply
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="team-snapshot-action-btn"
              @click="clearFilters"
            >
              Clear
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="team-snapshot-action-btn"
              :disabled="!punchFromDate || !punchToDate || downloadLoading"
              @click="downloadPunchData"
            >
              <b-spinner v-if="downloadLoading" small class="mr-25" />
              <feather-icon v-else icon="DownloadIcon" size="12" class="mr-25" />
              CSV
            </b-button>
          </div>
        </div>
      </section>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BButton,
  BButtonGroup,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BSpinner,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import {
  filterActiveProbationRows,
  formatProbationDaysLabel,
  isProbationStatusCompleted,
  computeProbationTiming,
} from '@/utils/probation-timing'

const PROBATION_VISIBLE_CAP = 5
const ALERT_VISIBLE_CAP = 4
const EXTRA_VISIBLE_CAP = 3

function fmtDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'TeamLeaderAlertsPanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BSpinner,
    BListGroup,
    BListGroupItem,
  },
  props: {
    panelTitle: {
      type: String,
      default: 'Team snapshot',
    },
    pendingLeaveCount: {
      type: Number,
      default: 0,
    },
    /** People currently on probation (Needs attention list) */
    probationEnding: {
      type: Array,
      default: () => [],
    },
    probationSourceMissing: {
      type: Boolean,
      default: false,
    },
    leaveConflicts: {
      type: Array,
      default: () => [],
    },
    /**
     * @deprecated Probation people live only under Needs attention.
     * Kept for prop compatibility; ignored by Alerts & Notifications.
     */
    probationAlerts: {
      type: Array,
      default: () => [],
    },
    frequentRegularization: {
      type: Array,
      default: () => [],
    },
    upcomingLeavesNext7: {
      type: Array,
      default: () => [],
    },
    birthdaysThisWeek: {
      type: Array,
      default: () => [],
    },
    announcementCount: {
      type: Number,
      default: 0,
    },
    alertItemLimit: {
      type: Number,
      default: ALERT_VISIBLE_CAP,
    },
    teamMembers: {
      type: Array,
      default: () => [],
    },
    getUserName: {
      type: Function,
      required: true,
    },
    formatDate: {
      type: Function,
      required: true,
    },
    punchFilterActive: {
      type: Boolean,
      default: false,
    },
    downloadLoading: {
      type: Boolean,
      default: false,
    },
    showPunchFilters: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const today = new Date()
    const narrow = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 991.98px)').matches
      : false
    return {
      showProbationList: false,
      probationListExpanded: false,
      PROBATION_VISIBLE_CAP,
      /** Phone/tablet (≤991): stays collapsed until user toggles — never auto-expand */
      mobileSnapshotCollapsed: true,
      isNarrowViewport: narrow,
      punchFromDate: fmtDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      punchToDate: fmtDate(today),
      punchEmployeeFilter: null,
      activePreset: 'month',
      datePresets: [
        { label: 'Today', value: 'today' },
        { label: 'Week', value: 'week' },
        { label: 'Month', value: 'month' },
      ],
    }
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this._snapshotMq = window.matchMedia('(max-width: 991.98px)')
    this._onSnapshotMq = e => {
      const nowNarrow = e.matches
      // Entering mobile/tablet: force closed (do not carry desktop "open" body)
      if (nowNarrow && !this.isNarrowViewport) {
        this.mobileSnapshotCollapsed = true
      }
      this.isNarrowViewport = nowNarrow
    }
    this.isNarrowViewport = this._snapshotMq.matches
    // Remount / first paint on narrow: always start collapsed
    if (this.isNarrowViewport) {
      this.mobileSnapshotCollapsed = true
    }
    if (this._snapshotMq.addEventListener) {
      this._snapshotMq.addEventListener('change', this._onSnapshotMq)
    } else if (this._snapshotMq.addListener) {
      this._snapshotMq.addListener(this._onSnapshotMq)
    }
  },
  beforeDestroy() {
    if (!this._snapshotMq || !this._onSnapshotMq) return
    if (this._snapshotMq.removeEventListener) {
      this._snapshotMq.removeEventListener('change', this._onSnapshotMq)
    } else if (this._snapshotMq.removeListener) {
      this._snapshotMq.removeListener(this._onSnapshotMq)
    }
  },
  computed: {
    /** People on probation — Null / Extended only (Needs attention). */
    activeProbationEnding() {
      return filterActiveProbationRows(this.probationEnding || [])
    },
    probationCount() {
      return this.activeProbationEnding.length
    },
    attentionTotal() {
      return (this.pendingLeaveCount || 0) + this.probationCount
    },
    visibleProbationItems() {
      const items = this.activeProbationEnding
      if (this.probationListExpanded) return items
      return items.slice(0, PROBATION_VISIBLE_CAP)
    },
    hiddenProbationCount() {
      if (this.probationListExpanded) return 0
      return Math.max(0, this.probationCount - PROBATION_VISIBLE_CAP)
    },
    /**
     * Alerts & Notifications: conflicts + frequent reg only.
     * Probation people are listed solely under Needs attention (no PROBATION rows here).
     */
    alertItems() {
      const items = []

      ;(this.leaveConflicts || []).forEach((conflict, index) => {
        const employees = conflict.employees || []
        const names = employees
          .map(emp => this.getUserName(emp.emp_id) || emp.emp_id)
          .filter(Boolean)
        const dateLabel = conflict.date ? this.formatDate(conflict.date) : 'Upcoming'
        items.push({
          key: `conflict-${conflict.date || index}`,
          type: 'leave_conflict',
          typeLabel: 'Conflict',
          badgeVariant: 'light-danger',
          severityVariant: 'danger',
          severityLabel: 'High',
          primary: `${employees.length} on leave · ${dateLabel}`,
          reason: names.length
            ? names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '')
            : 'Overlapping leave',
          sortRank: 1,
        })
      })

      ;(this.frequentRegularization || []).forEach((item, index) => {
        const name = this.getUserName(item.emp_id) || item.emp_id || 'Employee'
        const count = item.count != null
          ? item.count
          : (item.regularization_count != null ? item.regularization_count : null)
        items.push({
          key: `freq-reg-${item.emp_id || index}`,
          type: 'frequent_reg',
          typeLabel: 'Reg.',
          badgeVariant: 'light-secondary',
          severityVariant: 'secondary',
          severityLabel: 'Watch',
          primary: name,
          reason: count != null
            ? `${count} regularizations in last 30 days`
            : 'Frequent regularization',
          sortRank: 4,
        })
      })

      return items.sort((a, b) => a.sortRank - b.sortRank)
    },
    alertTotalCount() {
      return this.alertItems.length
    },
    visibleAlertItems() {
      return this.alertItems.slice(0, this.alertItemLimit)
    },
    hiddenAlertCount() {
      return Math.max(0, this.alertTotalCount - this.alertItemLimit)
    },
    headerBadgeCount() {
      return this.attentionTotal + this.alertTotalCount
    },
    visibleUpcomingLeaves() {
      return (this.upcomingLeavesNext7 || []).slice(0, EXTRA_VISIBLE_CAP)
    },
    hiddenUpcomingLeavesCount() {
      return Math.max(0, (this.upcomingLeavesNext7 || []).length - EXTRA_VISIBLE_CAP)
    },
    visibleBirthdays() {
      return (this.birthdaysThisWeek || []).slice(0, EXTRA_VISIBLE_CAP)
    },
    hiddenBirthdaysCount() {
      return Math.max(0, (this.birthdaysThisWeek || []).length - EXTRA_VISIBLE_CAP)
    },
    showExtrasSection() {
      return (this.upcomingLeavesNext7 || []).length > 0
        || (this.birthdaysThisWeek || []).length > 0
        || (this.announcementCount || 0) > 0
    },
    employeeOptions() {
      const options = [{ value: null, text: 'All employees' }]
      ;(this.teamMembers || []).forEach(member => {
        const id = member && (member.emp_id || member.id)
        if (!id) return
        options.push({
          value: id,
          text: this.getUserName(id) || id,
        })
      })
      return options
    },
    canApplyRosterFilter() {
      return !!this.punchEmployeeFilter
    },
  },
  watch: {
    probationCount(val) {
      if (!val) {
        this.showProbationList = false
        this.probationListExpanded = false
      }
    },
  },
  methods: {
    toggleMobileSnapshot() {
      if (!this.isNarrowViewport) return
      this.mobileSnapshotCollapsed = !this.mobileSnapshotCollapsed
    },
    onPendingLeavesClick() {
      if (this.pendingLeaveCount <= 0) return
      this.$emit('pending-leaves-click')
    },
    toggleProbationList() {
      if (this.probationCount <= 0) return
      this.showProbationList = !this.showProbationList
      if (!this.showProbationList) this.probationListExpanded = false
    },
    collapseProbationList() {
      this.probationListExpanded = false
    },
    /** Open Manage Users where HR/managers handle probation status (On Probation filter). */
    openProbationManagement() {
      this.$router.push({
        name: 'apps-users-list',
        query: { probation: '1' },
      }).catch(err => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
    probationName(item) {
      if (!item) return 'Employee'
      return this.getUserName(item.emp_id) || item.emp_id || 'Employee'
    },
    probationDaysLabel(item) {
      if (!item) return ''
      if (isProbationStatusCompleted(item)) return 'Accepted'
      const timing = computeProbationTiming(item)
      const days = timing.daysRemaining != null
        ? timing.daysRemaining
        : (item.daysRemaining != null ? item.daysRemaining : item.days_remaining)
      const endDate = timing.probationEndDate
        || item.probationEndDate
        || item.probation_end_date
      return formatProbationDaysLabel(days, endDate, this.formatDate, item)
    },
    leaveRowPrimary(leave) {
      if (!leave) return 'Leave'
      const name = this.getUserName(leave.emp_id) || leave.emp_id || 'Employee'
      const type = leave.leave_type || leave.type || 'Leave'
      return `${name} · ${type}`
    },
    leaveRowTitle(leave) {
      return this.leaveRowPrimary(leave)
    },
    leaveRowDate(leave) {
      if (!leave || !leave.from_date) return ''
      const from = this.formatDate(leave.from_date)
      if (leave.to_date && leave.to_date !== leave.from_date) {
        return `${from}–${this.formatDate(leave.to_date)}`
      }
      return from
    },
    setDatePreset(preset) {
      const today = new Date()
      if (preset === 'today') {
        this.punchFromDate = fmtDate(today)
        this.punchToDate = fmtDate(today)
      } else if (preset === 'week') {
        const day = today.getDay()
        const mon = new Date(today)
        mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1))
        this.punchFromDate = fmtDate(mon)
        this.punchToDate = fmtDate(today)
      } else if (preset === 'month') {
        this.punchFromDate = fmtDate(new Date(today.getFullYear(), today.getMonth(), 1))
        this.punchToDate = fmtDate(today)
      }
      this.activePreset = preset
    },
    applyRosterFilter() {
      this.$emit('apply-punch-filter', {
        empId: this.punchEmployeeFilter || null,
      })
    },
    clearFilters() {
      this.punchEmployeeFilter = null
      this.setDatePreset('month')
      this.$emit('clear-punch-filter')
    },
    downloadPunchData() {
      if (!this.punchFromDate || !this.punchToDate) return
      this.$emit('download-punch-data', {
        from: this.punchFromDate,
        to: this.punchToDate,
        empId: this.punchEmployeeFilter || null,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.team-leader-alerts-panel {
  border: 1px solid #e8ecf0;
  box-shadow: none;
  border-radius: 10px;
  overflow: hidden;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.team-leader-alerts-panel :deep(.card-header) {
  background: #fff;
  flex-shrink: 0;
}

.team-leader-alerts-panel :deep(.card-header) h6 {
  font-size: 0.8rem;
  font-weight: 600;
}

.team-alerts-count-badge {
  font-size: 0.55rem;
  font-weight: 700;
}

.team-leader-alerts-panel :deep(.card-body) {
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.team-snapshot-section-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: #6b7280;
  margin-bottom: 0.3rem;
  line-height: 1.2;
}

.team-snapshot-attention,
.team-snapshot-alerts,
.team-snapshot-extras {
  padding: 0.15rem 0 0.5rem;
  margin-bottom: 0.4rem;
  border-bottom: 1px solid #e8ecf0;
}

.team-snapshot-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
}

.team-snapshot-metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
  padding: 0.32rem 0.5rem;
  border: 1px solid #eef1f4;
  border-radius: 7px;
  background: #fff;
  text-align: left;
  cursor: default;
  transition: background 0.12s ease, border-color 0.12s ease;
}

button.team-snapshot-metric:not(:disabled) {
  cursor: pointer;
}

button.team-snapshot-metric:not(:disabled):hover {
  background: #f0fdfa;
  border-color: #ccfbf1;
}

.team-snapshot-metric--muted,
.team-snapshot-metric:disabled {
  opacity: 1;
  cursor: default;
  background: #f9fafb;
  border-color: #f0f2f5;
}

.team-snapshot-metric--muted .team-snapshot-metric__label {
  color: #9ca3af;
  font-weight: 500;
}

.team-snapshot-metric--active {
  background: #fafbfc;
}

.team-snapshot-metric--open {
  background: #f0fdfa;
  border-color: #99f6e4;
}

.team-snapshot-metric__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
}

.team-snapshot-metric__chevron {
  color: #0f766e;
  flex-shrink: 0;
}

.team-snapshot-metric__badge {
  font-size: 0.58rem;
  font-weight: 700;
  min-width: 1.35rem;
  justify-content: center;
}

.team-snapshot-probation-list {
  border: 1px solid #d1fae5;
  border-radius: 6px;
  background: #f0fdfa;
  padding: 0.3rem 0.4rem;
  max-height: 100px;
  overflow-y: auto;
}

.team-snapshot-probation-list--expanded {
  max-height: 220px;
}

.team-snapshot-probation-row,
.team-snapshot-extra-row {
  padding: 0.14rem 0.1rem;
  gap: 0.35rem;
}

.team-snapshot-probation-row + .team-snapshot-probation-row,
.team-snapshot-extra-row + .team-snapshot-extra-row {
  border-top: 1px solid #d1fae5;
}

.team-snapshot-extra-row {
  cursor: pointer;
  border-radius: 4px;
}

.team-snapshot-extra-row:hover {
  background: #f0fdfa;
}

.team-snapshot-probation-days {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 0.65rem !important;
}

.team-snapshot-probation-more {
  font-size: 0.6rem !important;
  font-weight: 600;
}

button.team-snapshot-probation-more {
  display: inline-block;
  margin-top: 0.15rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f766e;
  font-weight: 700;
  line-height: 1.3;
  cursor: pointer;
  text-align: left;
}

button.team-snapshot-probation-more:hover,
button.team-snapshot-probation-more:focus {
  color: #115e59;
  text-decoration: underline;
  outline: none;
}

.team-snapshot-probation-empty,
.team-snapshot-all-clear {
  font-size: 0.62rem !important;
  line-height: 1.3;
  padding: 0.2rem 0.1rem;
  display: flex;
  align-items: center;
}

.team-alerts-list {
  max-height: 132px;
  overflow-y: auto;
  border: 1px solid #eef1f4;
  border-radius: 7px;
  background: #fafbfc;
}

.team-alert-type-badge {
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.18rem 0.3rem;
  margin-top: 0.1rem;
}

.team-alert-severity-badge {
  font-size: 0.5rem;
  font-weight: 700;
}

.team-alerts-more {
  font-size: 0.6rem !important;
  font-weight: 600;
}

.team-snapshot-extra-block {
  border: 1px solid #eef1f4;
  border-radius: 7px;
  background: #fafbfc;
  padding: 0.35rem 0.4rem;
  margin-bottom: 0.3rem;
}

.team-snapshot-extra-block:last-child {
  margin-bottom: 0;
}

.team-snapshot-extra-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: #374151;
}

.team-snapshot-extra-block .team-snapshot-extra-row + .team-snapshot-extra-row {
  border-top-color: #eef1f4;
}

.team-snapshot-punch {
  min-height: 0;
  padding-top: 0.05rem;
  overflow-y: auto;
  gap: 0.4rem;
}

.team-snapshot-punch-head {
  margin-bottom: 0.05rem;
}

.team-snapshot-block {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.4rem 0.4rem 0.45rem;
  border: 1px solid #eef1f4;
  border-radius: 7px;
  background: #fafbfc;
}

.team-snapshot-punch-presets {
  display: flex;

  :deep(.btn) {
    flex: 1 1 0;
  }
}

.team-snapshot-preset-btn {
  font-size: 0.62rem;
  font-weight: 600;
  padding: 0.18rem 0.3rem;
  line-height: 1.2;
}

.team-snapshot-dates {
  gap: 0.3rem;
}

.team-snapshot-date-field {
  flex: 1 1 0;
  min-width: 0;
}

.team-snapshot-field-label {
  display: block;
  font-size: 0.58rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
  margin-bottom: 0.12rem !important;
}

.team-snapshot-punch :deep(.form-control),
.team-snapshot-punch :deep(.custom-select) {
  font-size: 0.68rem;
  min-height: 1.65rem;
  padding-top: 0.12rem;
  padding-bottom: 0.12rem;
  padding-left: 0.28rem;
  padding-right: 1.1rem;
  pointer-events: auto;
  cursor: pointer;
  background-color: #fff;
}

.team-snapshot-punch-footer {
  gap: 0.35rem;
  padding-top: 0.15rem;
}

.team-snapshot-employee-field {
  min-width: 0;
}

.team-snapshot-punch-actions {
  gap: 0.2rem;
}

.team-snapshot-action-btn {
  font-size: 0.62rem;
  font-weight: 600;
  padding: 0.2rem 0.38rem;
  line-height: 1.2;
  white-space: nowrap;
}

.team-snapshot-probation-list::-webkit-scrollbar,
.team-alerts-list::-webkit-scrollbar,
.team-snapshot-punch::-webkit-scrollbar {
  width: 3px;
}

.team-snapshot-probation-list::-webkit-scrollbar-thumb,
.team-alerts-list::-webkit-scrollbar-thumb,
.team-snapshot-punch::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.min-h-0 {
  min-height: 0;
}

@media (max-width: 1199px) {
  .team-snapshot-punch-footer {
    flex-wrap: wrap;
  }

  .team-snapshot-employee-field {
    flex: 1 1 100%;
  }

  .team-snapshot-punch-actions {
    width: 100%;
  }

  .team-snapshot-punch-actions .team-snapshot-action-btn {
    flex: 1 1 0;
  }
}

@media (max-width: 991px) {
  .team-leader-alerts-panel {
    height: auto;
    max-height: none;
  }

  .team-mid-panel__header--toggle {
    cursor: pointer;
    user-select: none;
    min-height: 2.5rem;
    gap: 0.5rem;
  }

  .team-mid-panel__header--toggle:hover {
    background: #f8fafb;
  }

  .team-mid-panel__header--toggle:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(0, 172, 153, 0.25);
  }

  .team-snapshot-collapse-chevron {
    margin-left: auto;
  }

  .team-snapshot-panel--mobile-collapsed {
    height: auto !important;
    min-height: 0 !important;
  }

  /* CSS backup: keep body hidden on ≤991 while collapsed (covers first paint / v-show races) */
  .team-snapshot-panel.team-snapshot-panel--mobile-collapsed > .card-body,
  .team-snapshot-panel.team-snapshot-panel--mobile-collapsed .team-mid-panel__body {
    display: none !important;
  }

  .team-snapshot-punch {
    overflow-y: visible;
  }

  .team-snapshot-dates {
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .team-snapshot-date-field {
    flex: 1 1 calc(50% - 0.2rem);
    min-width: 8.5rem;
  }

  .team-snapshot-punch :deep(.form-control),
  .team-snapshot-punch :deep(.custom-select) {
    font-size: 0.75rem;
    min-height: 2rem;
    width: 100%;
    max-width: 100%;
  }

  .team-snapshot-action-btn {
    min-height: 2rem;
    font-size: 0.68rem;
    padding: 0.3rem 0.45rem;
  }
}

@media (max-width: 575px) {
  .team-snapshot-dates {
    flex-direction: column;
  }

  .team-snapshot-date-field {
    flex: 1 1 100%;
    min-width: 0;
    width: 100%;
  }

  .team-snapshot-punch-presets {
    flex-wrap: wrap;
  }

  .team-snapshot-preset-btn {
    flex: 1 1 30%;
    min-height: 2rem;
    font-size: 0.68rem;
  }

  .team-snapshot-punch-actions {
    flex-wrap: wrap;
  }

  .team-snapshot-punch-actions .team-snapshot-action-btn {
    flex: 1 1 calc(33.33% - 0.15rem);
    min-width: 4.5rem;
  }
}
</style>
